import {Inject, Injectable, InjectionToken} from "@angular/core";
import {
    catchError, EMPTY,
    identity,
    map,
    mergeMap,
    Observable,
    of,
    ReplaySubject,
    share,
    Subject,
    throwError,
    timeout
} from "rxjs";
import {LocalConfigurationService} from "../configuration/local-configuration.service";
import {ResourceModule} from "../resource/resource.module";
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse} from "@angular/common/http";
import {fromPromise} from "rxjs/internal/observable/innerFrom";

import {DataClientError} from "../error/data-client-error";

export class AuthConfig {
    httpTimeout: number;
    getDeviceId?: () => Promise<string>;
    getActorType?: () => Promise<string>;
}

export enum LoginType {
    USER,
    GUEST,
    MEMBER_EMAIL,
    MEMBER_MOBILE,
    MEMBER_NO,
    MEMBER_NO_PLUS_MOBILE,
    MEMBER_DELEGATED,
    POS_PIN
}

export class LoginDetails {
    user: string;
    userId: number;
    identityId: string;
    role: string;
    roles: string[];
    member: string;
    locationId: number;
    outletId: number;
    webTerminalId: number;

    constructor(user: string, userId: number, identityId: string, role: string, roles: string[], member: string, locationId: number, outletId: number, webTerminalId: number) {
        this.user = user;
        this.userId = userId;
        this.identityId = identityId;
        this.role = role;
        this.roles  = roles;
        this.member = member;
        this.locationId = locationId;
        this.outletId = outletId;
        this.webTerminalId = webTerminalId;
    }
}

export interface LoginOptions {
    appId?: number;
    webTerminalName?: string;
    identity?: string;
    identityPassword?: string;
    userPin?: string;
    remember?: boolean;
    verificationId?: string;
    verificationCode?: string;
}

export interface RefreshOptions {
    loginType?: LoginType;
    userPin?: string;
}

export class Authorisation {
    constructor(public authKey?: string) {
    }
}

/**
 * Authentication Service to obtain access_token and refresh_token.
 */
@Injectable()
export class AuthService {
    private static readonly LOGIN_URL: string = '/dataserver/auth/login';
    private static readonly LOGOUT_URL: string = '/dataserver/auth/logout';
    private static readonly REFRESH_URL: string = '/dataserver/auth/refresh';

    private static readonly PRIMARY_USER_ACCESS_STORAGE_KEY: string = 'primaryUserAccessStorage';
    private static readonly DIRECT_ACCESS_STORAGE_KEY: string = 'accessStorage';
    private static readonly DIRECT_REFRESH_STORAGE_KEY: string = 'authStorage';
    private static readonly DEVICE_ACCESS_STORAGE_KEY: string = 'deviceAccessStorage';
    private static readonly DEVICE_REFRESH_STORAGE_KEY: string = 'deviceAuthStorage';
    private static readonly DATASERVER_URL_STORAGE_KEY: string = 'dataserverURLStorage';
    private static readonly RESTRICTED_OUTLETS_STORAGE_KEY: string = 'RestrictedOutlets';
    private static readonly RESTRICTED_CUSTOM_OUTLET_GROUPS_STORAGE_KEY: string = 'RestrictedCustomOutletGroups';

    private static readonly HEADER_AUTHORIZATION: string = 'Authorization';
    private static readonly HEADER_LOCATIONCONTEXT_LOCATIONID: string = 'LocationContextLocationId';
    private static readonly HEADER_LOCATIONCONTEXT_OUTLETID: string = 'LocationContextOutletId';
    private static readonly HEADER_LOCATIONCONTEXT_WEBTERMINALID: string = 'LocationContextWebTerminalId';
    private static readonly HEADER_LOCATIONCONTEXT_DEFAULTLOCALDATETIME: string = 'LocationContextDefaultLocalDateTime';

    private deviceId: string;
    private actorType: string;
    private _dataserverURL: string;
    private primaryUserAccessToken: string;
    private accessToken: string;
    private refreshToken: string;
    private locationId: number;
    private outletId: number;
    private webTerminalId: number;
    private defaultLocalDateTime: string;
    private loginChecked: boolean = false;
    private refreshOptions: RefreshOptions = {};

    private restrictedOutlets: number[];
    private restrictedCustomOutletGroups: number[];
    private storage: Storage;
    private pathName: string;
    private accessTokenRefreshObservable: Observable<Authorisation>;
    private _isRestrictedLocalisation: boolean;
    private _tokensLoaded$: Subject<void> = new ReplaySubject(1);
    private _loginChanging$: Subject<boolean> = new Subject<boolean>();
    private _loginDetails$: Subject<LoginDetails> = new Subject<LoginDetails>();
    private _currentLoginDetails$: Subject<LoginDetails> = new ReplaySubject(1);
    private _loginRevoked$: Subject<boolean> = new Subject<boolean>();
    private _compatibility$: Subject<boolean> = new Subject<boolean>();

    constructor(private httpClient: HttpClient,
                private localConfigurationService: LocalConfigurationService,
                @Inject(ResourceModule.RESOURCE_NAMESPACE_KEY) protected nameSpace: string,
                @Inject(ResourceModule.ENTITY_NAMES_KEY) private entityNames: string[],
                @Inject(ResourceModule.SEARCH_NAMES_KEY) private searchNames: string[],
                @Inject(ResourceModule.ACTION_NAMES_KEY) private actionNames: string[],
                private authConfig: AuthConfig) {
        this.pathName = window.location.pathname;
        this.storage = localStorage;

        this.loadTokens();
    }

    private async loadTokens(): Promise<void> {
        this.deviceId = this.authConfig && this.authConfig.getDeviceId ? await this.authConfig.getDeviceId() : undefined;
        this.actorType = this.authConfig && this.authConfig.getActorType() ? await this.authConfig.getActorType() : undefined;

        // set tokens if saved in local storage
        const primaryUserAccessStorage: any = JSON.parse(this.storage.getItem(this.primaryUserAccessStorageKey));
        this.primaryUserAccessToken = primaryUserAccessStorage && primaryUserAccessStorage.primary_user_access_token;

        const accessStorage: any = JSON.parse(this.storage.getItem(this.accessStorageKey));
        this.accessToken = accessStorage && accessStorage.device_id === this.deviceId && accessStorage.actor_type === this.actorType && accessStorage.access_token;

        const refreshStorage: any = JSON.parse(this.storage.getItem(this.refreshStorageKey));
        this.refreshToken = refreshStorage && refreshStorage.device_id === this.deviceId && refreshStorage.actor_type === this.actorType && refreshStorage.refresh_token;

        this.restrictedOutlets = JSON.parse(this.storage.getItem(this.restrictedOutletsStorageKey));
        this.restrictedCustomOutletGroups = JSON.parse(this.storage.getItem(this.restrictedCustomOutletGroupsStorageKey));

        this._isRestrictedLocalisation = this.restrictedOutlets != undefined;

        this._tokensLoaded$.next();
    }

    get isRestrictedLocalisation(): boolean {
        return this._isRestrictedLocalisation;
    }

    /**
     * Returns an observable indicating that auth tokens have been loaded
     * @returns {Observable}
     */
    get tokensLoaded$(): Observable<void> {
        return this._tokensLoaded$;
    }

    /**
     * Returns an observable sequence indicating that a login or logout has been initiated
     * @returns {Observable}
     */
    get loginChanging$(): Observable<boolean> {
        return this._loginChanging$;
    }

    /**
     * Returns an observable sequence of login details.
     * @returns {Observable}
     */
    get loginDetails$(): Observable<LoginDetails> {
        return this._loginDetails$;
    }

    /**
     * Returns a replay subject containing the current login details
     * @returns {Subject}
     */
    get currentLoginDetails$(): Subject<LoginDetails> {
        return this._currentLoginDetails$;
    }

    /**
     * Returns an observable sequence indicating that the user login has been revoked
     * @returns {Observable}
     */
    get loginRevoked$(): Observable<boolean> {
        return this._loginRevoked$;
    }

    /**
     * Returns an observable sequence of compatibility
     * @returns {Observable}
     */
    get compatibility$(): Observable<boolean> {
        return this._compatibility$;
    }

    private get primaryUserAccessStorageKey(): string {
        return this.pathName + "/" + AuthService.PRIMARY_USER_ACCESS_STORAGE_KEY;
    }

    private get accessStorageKey(): string {
        return this.pathName + "/" + (this.deviceId ? AuthService.DEVICE_ACCESS_STORAGE_KEY : AuthService.DIRECT_ACCESS_STORAGE_KEY);
    }

    private get refreshStorageKey(): string {
        return this.pathName + "/" + (this.deviceId ? AuthService.DEVICE_REFRESH_STORAGE_KEY : AuthService.DIRECT_REFRESH_STORAGE_KEY);
    }

    private get dataserverUrlStorageKey(): string {
        return this.pathName + "/" + AuthService.DATASERVER_URL_STORAGE_KEY;
    }

    private get restrictedOutletsStorageKey(): string {
        return this.pathName + "/" + AuthService.RESTRICTED_OUTLETS_STORAGE_KEY;
    }

    private get restrictedCustomOutletGroupsStorageKey(): string {
        return this.pathName + "/" + AuthService.RESTRICTED_CUSTOM_OUTLET_GROUPS_STORAGE_KEY;
    }

    get dataserverURL(): string {
        const storedDataserverURL: string = this.storage.getItem(this.dataserverUrlStorageKey);
        if (!this._dataserverURL) {
            this._dataserverURL = storedDataserverURL;
        } else if (this._dataserverURL !== storedDataserverURL) {
            this.dataserverURL = this._dataserverURL;
        }
        return this._dataserverURL;
    }

    set dataserverURL(dataserverURL: string) {
        this._dataserverURL = dataserverURL || '';
        this.storage.setItem(this.dataserverUrlStorageKey, this._dataserverURL);
    }

    notifyLoginDetailsChange(): void {
        const loginDetails: LoginDetails = new LoginDetails(this.getLoggedInUserName(), this.getLoggedInUserId(), this.getLoggedInIdentityId(),
            this.getLoggedInRole(), this.getLoggedInRoles(), this.getLoggedInMember(),
            this.getLoggedInLocationId(), this.getLoggedInOutletId(), this.getLoggedInWebTerminalId());
        this._loginDetails$.next(loginDetails);
        this._currentLoginDetails$.next(loginDetails);
    }

    login(user: string, userPassword: string, loginType: LoginType, options: LoginOptions = {}): Observable<Authorisation> {
        console.log("Logging in...");

        this._loginChanging$.next(true);

        if (loginType === LoginType.POS_PIN) {

            // Store the current access token as the primary user access token (will be restored on logout of the pos pin user)
            if (this.getLoggedInType() === LoginType.USER) {
                this.primaryUserAccessToken = this.accessToken;
                this.storage.setItem(this.primaryUserAccessStorageKey, JSON.stringify({
                    primary_user_access_token: this.primaryUserAccessToken
                }));
            }

            // Get a new access token for the POS pin user
            return this.refreshAccessToken({
                loginType: loginType,
                userPin: options.userPin
            });
        }

        if (options.remember === false) {
            this.storage = sessionStorage;
        }

        let headers: HttpHeaders = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');

        return fromPromise(this.localConfigurationService.getString('local/dataserverURL').then((dataserverURL: string) => {
            this.dataserverURL = dataserverURL;
            return this.httpClient.post(this.dataserverURL + AuthService.LOGIN_URL, JSON.stringify({
                user: user,
                userPassword: userPassword,
                appId: options.appId,
                deviceId: this.deviceId,
                actorType: this.actorType,
                webTerminalName: options.webTerminalName,
                appName: this.localConfigurationService.application,
                configContextNames: this.localConfigurationService.contextNames,
                loginType: LoginType[loginType],
                identity: options.identity,
                identityPassword: options.identityPassword,
                verificationId: options.verificationId,
                verificationCode: options.verificationCode,
                entityResourceNames: this.includeNameSpaceToResourceNames(this.entityNames),
                actionResourceNames: this.includeNameSpaceToResourceNames(this.actionNames),
                searchResourceNames: this.includeNameSpaceToResourceNames(this.searchNames)
            }), {
                headers: headers,
                observe: "response",
                responseType: "json"
            }).pipe(
                (this.authConfig && this.authConfig.httpTimeout ? timeout(this.authConfig.httpTimeout) : identity),
                map((response: HttpResponse<Object>) => {
                    // login successful if there's a jwt token in the response
                    const receivedAccessToken: string = response.body && response.body["access_token"];
                    const receivedRefreshToken: string = response.body && response.body["refresh_token"];
                    const authKey: string = response.body && response.body["auth_key"];

                    if (receivedAccessToken && receivedRefreshToken) {
                        // set token property
                        this.accessToken = receivedAccessToken;
                        this.refreshToken = receivedRefreshToken;

                        this.storage.setItem(this.accessStorageKey, JSON.stringify({
                            device_id: this.deviceId,
                            actor_type: this.actorType,
                            access_token: receivedAccessToken
                        }));

                        this.storage.setItem(this.refreshStorageKey, JSON.stringify({
                            user: user,
                            identity: options.identity,
                            device_id: this.deviceId,
                            actor_type: this.actorType,
                            refresh_token: receivedRefreshToken
                        }));

                        this.notifyLoginDetailsChange();

                        return new Authorisation(authKey);
                    } else {
                        //this is not meant to happen. It means server message protocol has been changed.
                        this.logout();
                        return undefined;
                    }
                }),
                catchError((response: HttpErrorResponse) => {
                    if (response && response.status === 406) {
                        this._compatibility$.next(false)
                    } else {
                        this.logout();
                    }
                    return throwError(() => new DataClientError(response, "json"));
                })
            );
        })).pipe(
            mergeMap((result: Observable<Authorisation>) => result)
        );
    }

    /**
     * Client side detection whether access token is expired or about to expire
     * (i.e. considers expired a minute ahead of actual expiry).
     *
     * @returns {boolean}
     */
    isAccessTokenExpired(): Boolean {
        if (!this.accessToken) {
            return true; //consider expired if we are not authenticated yet ...
        }
        let parsedToken: any = this.parseToken(this.accessToken);
        if (!parsedToken) {
            return true; //consider expired if access_token is not in valid jwt format.
        }
        //divide by 1000 because of the way jwt dates are stored
        //subtract 60 seconds (i.e. 1 minutes) to expire token a minute ahead of actual expiry ...
        return (parsedToken.exp - 60) <= Math.floor(Date.now() / 1000);
    }

    getRestrictedOutlets(): number[] {
        return this.restrictedOutlets;
    }

    getRestrictedCustomOutletGroups(): number[] {
        return this.restrictedCustomOutletGroups;
    }

    refreshAccessToken(options?: RefreshOptions): Observable<Authorisation> {
        if (this.refreshToken == null) {
            return throwError(() => new Error("Attempted access-token refresh without a refresh-token"));
        }
        if (options) {
            this.refreshOptions = options;
        }

        let headers: HttpHeaders = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');

        if (this.accessTokenRefreshObservable) {
            return this.accessTokenRefreshObservable;
        }
        this.accessTokenRefreshObservable = fromPromise(this.localConfigurationService.getString('local/dataserverURL').then((dataserverURL: string) => {
            this.dataserverURL = dataserverURL;
            return this.httpClient.post(this.dataserverURL + AuthService.REFRESH_URL, JSON.stringify({
                refresh_token: this.refreshToken,
                loginType: this.refreshOptions.loginType ? LoginType[this.refreshOptions.loginType] : undefined,
                userPin: this.refreshOptions.userPin,
                entityResourceNames: this.includeNameSpaceToResourceNames(this.entityNames),
                actionResourceNames: this.includeNameSpaceToResourceNames(this.actionNames),
                searchResourceNames: this.includeNameSpaceToResourceNames(this.searchNames),
                restrictedOutlets: this.restrictedOutlets,
                restrictedCustomOutletGroups: this.restrictedCustomOutletGroups
            }), {
                headers: headers,
                observe: "response",
                responseType: "json"
            }).pipe(
                (this.authConfig && this.authConfig.httpTimeout ? timeout(this.authConfig.httpTimeout) : identity),
                mergeMap((response: HttpResponse<Object>) => {

                    // login successful if there's a jwt token in the response
                    let receivedAccessToken = response.body && response.body["access_token"];
                    if (receivedAccessToken) {
                        //console.log("Refresh token request successful ... ");
                        // set token property
                        this.accessToken = receivedAccessToken;

                        // store access token in local storage
                        this.storage.setItem(this.accessStorageKey, JSON.stringify({
                            device_id: this.deviceId,
                            actor_type: this.actorType,
                            access_token: this.accessToken
                        }));

                        this.accessTokenRefreshObservable = null;

                        if (this.refreshOptions.loginType === LoginType.POS_PIN) {
                            this.notifyLoginDetailsChange();
                        }

                        // check if the restriction was successful
                        if (this.isRestrictedLocalisation) {
                            if (!this.isRestrictedLocalisationToken()) {
                                this.clearInternalRestrictedLocalisation();
                            }
                        }

                        return of(new Authorisation());
                    } else {
                        //this is not meant to happen.
                        this.accessTokenRefreshObservable = null;
                        return of(undefined);
                    }
                }),
                catchError((response: HttpErrorResponse) => {
                    if (response && response.status === 406) {
                        this._compatibility$.next(false);
                    } else if (response && (response.status === 400 || response.status === 401) && this.refreshOptions.loginType !== LoginType.POS_PIN) {

                        // We have our refresh token expired or revoked ... we should logout and rethrow this error further ...
                        this.logout();
                        this._loginRevoked$.next(true);
                    }
                    this.accessTokenRefreshObservable = null;
                    return throwError(() => new DataClientError(response, "json"));
                }));
        })).pipe(
            mergeMap((result: Observable<Authorisation>) => result),
            share({
                connector: () => new ReplaySubject(1),
                resetOnError: false,
                resetOnComplete: false,
                resetOnRefCountZero: false
            })
        );
        return this.accessTokenRefreshObservable;
    }

    /**
     * Set the location context for this session.
     * @param locationId
     * @param outletId
     * @param webTerminalId
     */
    setLocationContext(locationId: number, outletId: number, webTerminalId: number): void {
        if (locationId != undefined) {
            this.locationId = locationId;
        }
        if (outletId != undefined) {
            this.outletId = outletId;
        }
        if (webTerminalId != undefined) {
            this.webTerminalId = webTerminalId;
        }
        this.refreshAccessToken({}).subscribe({
            next: () => {
            },
            error: (error) => {
            }
        });
    }

    /**
     * Clear the location context
     */
    clearLocationContext(): void {
        this.locationId = undefined;
        this.outletId = undefined;
        this.webTerminalId = undefined;
        this.refreshAccessToken({}).subscribe({
            next: () => {
            },
            error: (error) => {
            }
        });
    }

    setDefaultLocalDateTime(defaultLocalDateTime: string): void {
        if (defaultLocalDateTime != undefined) {
            this.defaultLocalDateTime = defaultLocalDateTime;
        }
        this.refreshAccessToken({}).subscribe({
            next: () => {
            },
            error: (error) => {
            }
        });
    }

    clearDefaultLocalDateTime(): void {
        this.defaultLocalDateTime = undefined;
        this.refreshAccessToken({}).subscribe({
            next: () => {
            },
            error: (error) => {
            }
        });
    }

    /**
     * Set the restricted localisation
     * @param {number[]} outlets
     * @param {number[]} customOutletGroups
     */
    setRestrictedLocalisation(outlets: number[], customOutletGroups: number[]): void {
        this.restrictedOutlets = outlets;
        this.restrictedCustomOutletGroups = customOutletGroups;

        this.storage.setItem(this.restrictedOutletsStorageKey, JSON.stringify(this.restrictedOutlets));
        this.storage.setItem(this.restrictedCustomOutletGroupsStorageKey, JSON.stringify(this.restrictedCustomOutletGroups));

        this._isRestrictedLocalisation = true;

        this.refreshAccessToken({}).subscribe({
            next: () => {
            },
            error: (error) => {
            }
        });
    }

    /**
     * Clear the restricted localisation
     */
    clearRestrictedLocalisation(): void {
        this.clearInternalRestrictedLocalisation();
        this.refreshAccessToken({}).subscribe({
            next: () => {
            },
            error: (error) => {
            }
        });
    }

    logout(): void {
        console.log("Logging out...");

        this._loginChanging$.next(true);

        if (this.getLoggedInType() === LoginType.POS_PIN) {
            // In theory, we should always have the primary user access token as we persist it during the POS_PIN login, but if not retrieve it now.
            if (!this.primaryUserAccessToken) {
                let primaryUserAccessStorage: any = JSON.parse(this.storage.getItem(this.primaryUserAccessStorageKey));
                this.primaryUserAccessToken = primaryUserAccessStorage && primaryUserAccessStorage.primary_user_access_token;
            }
            if (this.primaryUserAccessToken) {
                this.accessToken = this.primaryUserAccessToken;
                this.storage.setItem(this.accessStorageKey, JSON.stringify({
                    device_id: this.deviceId,
                    actor_type: this.actorType,
                    access_token: this.accessToken
                }));
                this.notifyLoginDetailsChange();
                return;
            }
        }

        if (this.isLoggedIn()) {
            this.httpClient.post(this.dataserverURL + AuthService.LOGOUT_URL, JSON.stringify({
                refresh_token: this.refreshToken
            }), {
                headers: new HttpHeaders().set('Content-Type', 'application/json'),
                observe: "response",
                responseType: "json"
            }).pipe(
                catchError((response: HttpErrorResponse) => {
                    console.log("Error sending logout request to server: ", response);
                    return EMPTY;
                })
            );
        }

        // clear token remove user from local storage to log user out
        this.accessToken = null;
        this.refreshToken = null;
        this.locationId = null;
        this.outletId = null;
        this.webTerminalId = null;
        this.storage.removeItem(this.accessStorageKey);
        this.storage.removeItem(this.refreshStorageKey);

        this.clearInternalRestrictedLocalisation();

        this.notifyLoginDetailsChange();
    }

    isLoggedIn(): boolean {
        if (!this.refreshToken) {
            return false;
        }
        if (!this.loginChecked) {
            this.notifyLoginDetailsChange();
            this.loginChecked = true;
        }
        return true;
    }

    getLoggedInUserId(): number {
        if (!this.accessToken) {
            return undefined;
        }
        let parsedToken: any = this.parseToken(this.accessToken);
        if (!parsedToken) {
            return undefined;
        }
        return parsedToken.userId;
    }

    getLoggedInUserName(): string {
        if (!this.accessToken) {
            return undefined;
        }
        let parsedToken: any = this.parseToken(this.accessToken);
        if (!parsedToken) {
            return undefined;
        }
        return parsedToken.username;
    }

    getLoggedInIdentityId(): string {
        if (!this.accessToken) {
            return undefined;
        }
        let parsedToken: any = this.parseToken(this.accessToken);
        if (!parsedToken) {
            return undefined;
        }
        return parsedToken.identityId;
    }

    getLoggedInRole(): string {
        if (!this.accessToken) {
            return undefined;
        }
        let parsedToken: any = this.parseToken(this.accessToken);
        if (!parsedToken) {
            return undefined;
        }
        return parsedToken.role;
    }

    getLoggedInRoles(): string[] {
        if (!this.accessToken) {
            return undefined;
        }
        let parsedToken: any = this.parseToken(this.accessToken);
        if (!parsedToken) {
            return undefined;
        }
        return parsedToken.roles;
    }

    getLoggedInMember(): string {
        if (!this.accessToken) {
            return undefined;
        }
        let parsedToken: any = this.parseToken(this.accessToken);
        if (!parsedToken) {
            return undefined;
        }
        return parsedToken.memberNo;
    }

    isUniversal(): boolean {
        if (!this.accessToken) {
            return undefined;
        }
        let parsedToken: any = this.parseToken(this.accessToken);
        if (!parsedToken) {
            return undefined;
        }
        return parsedToken.universal;
    }

    getOutlets(): number[] {
        if (!this.accessToken) {
            return undefined;
        }
        let parsedToken: any = this.parseToken(this.accessToken);
        if (!parsedToken) {
            return undefined;
        }
        return parsedToken.outlets;
    }

    getCustomOutletGroups(): number[] {
        if (!this.accessToken) {
            return undefined;
        }
        let parsedToken: any = this.parseToken(this.accessToken);
        if (!parsedToken) {
            return undefined;
        }
        return parsedToken.customOutletGroups;
    }

    getLoggedInLocationId(): number {
        if (this.locationId) {
            return this.locationId;
        }
        if (!this.accessToken) {
            return undefined;
        }
        let parsedToken: any = this.parseToken(this.accessToken);
        if (!parsedToken) {
            return undefined;
        }
        return parsedToken.locationId;
    }

    getLoggedInOutletId(): number {
        if (this.outletId) {
            return this.outletId;
        }
        if (!this.accessToken) {
            return undefined;
        }
        let parsedToken: any = this.parseToken(this.accessToken);
        if (!parsedToken) {
            return undefined;
        }
        return parsedToken.outletId;
    }

    getLoggedInWebTerminalId(): number {
        if (this.webTerminalId) {
            return this.webTerminalId;
        }
        if (!this.accessToken) {
            return undefined;
        }
        let parsedToken: any = this.parseToken(this.accessToken);
        if (!parsedToken) {
            return undefined;
        }
        return parsedToken.webTerminalId;
    }

    getLoggedInWebTerminalName(): number {
        if (!this.accessToken) {
            return undefined;
        }
        let parsedToken: any = this.parseToken(this.accessToken);
        if (!parsedToken) {
            return undefined;
        }
        return parsedToken.webTerminalName;
    }

    getLoggedInType(): LoginType {
        if (!this.accessToken) {
            return undefined;
        }
        let parsedToken: any = this.parseToken(this.accessToken);
        if (!parsedToken) {
            return undefined;
        }
        return LoginType[<string>parsedToken.loginType];
    }

    parseToken(token: any): any {
        var base64Url = token.split('.')[1];
        if (base64Url) {
            return JSON.parse(window.atob(base64Url.replace('-', '+').replace('_', '/')));
        }
        return undefined;
    }

    setAuthorizationAndLocationHeaders(headers: HttpHeaders): HttpHeaders {
        headers = headers.set(AuthService.HEADER_AUTHORIZATION, `Bearer ` + this.accessToken);
        if (this.locationId) {
            headers = headers.set(AuthService.HEADER_LOCATIONCONTEXT_LOCATIONID, this.locationId.toString());
        } else {
            headers = headers.delete(AuthService.HEADER_LOCATIONCONTEXT_LOCATIONID);
        }
        if (this.outletId) {
            headers = headers.set(AuthService.HEADER_LOCATIONCONTEXT_OUTLETID, this.outletId.toString());
        } else {
            headers = headers.delete(AuthService.HEADER_LOCATIONCONTEXT_OUTLETID);
        }
        if (this.webTerminalId) {
            headers = headers.set(AuthService.HEADER_LOCATIONCONTEXT_WEBTERMINALID, this.webTerminalId.toString());
        } else {
            headers = headers.delete(AuthService.HEADER_LOCATIONCONTEXT_WEBTERMINALID);
        }
        if (this.defaultLocalDateTime) {
            headers = headers.set(AuthService.HEADER_LOCATIONCONTEXT_DEFAULTLOCALDATETIME, this.defaultLocalDateTime);
        } else {
            headers = headers.delete(AuthService.HEADER_LOCATIONCONTEXT_DEFAULTLOCALDATETIME);
        }
        return headers;
    }

    private clearInternalRestrictedLocalisation(): void {
        this.restrictedOutlets = undefined;
        this.restrictedCustomOutletGroups = undefined;

        this._isRestrictedLocalisation = false;

        this.storage.removeItem(this.restrictedOutletsStorageKey);
        this.storage.removeItem(this.restrictedCustomOutletGroupsStorageKey);
    }

    private isRestrictedLocalisationToken(): boolean {
        if (!this.accessToken) {
            return undefined;
        }
        let parsedToken: any = this.parseToken(this.accessToken);
        if (!parsedToken) {
            return undefined;
        }
        return parsedToken.restrictedLocalisation;
    }

    private includeNameSpaceToResourceNames(names: string[]): string[] {
        return names ? names.map(value => this.nameSpace + "/" + value) : undefined;
    }
}
